// PlatformAnnouncements — 真實資料版（Route 15, PR #106）。

import React, { useCallback, useEffect, useState } from "react";
import { Megaphone, Send, RefreshCw, ServerCrash, Inbox } from "lucide-react";
import EmptyState from "../../components/EmptyState.js";
import Skeleton from "../../components/Skeleton.js";
import { Field } from "../../components/Field.js";
import { listAnnouncements, createAnnouncement, friendlyPlatformError, type PlatformAnnouncement } from "../../lib/platformApi.js";
import { formatTaipeiDateTime } from "../../../lib/time.js";

type ListState =
  | { status: "loading" }
  | { status: "ready"; rows: PlatformAnnouncement[] }
  | { status: "error"; message: string };

export default function PlatformAnnouncements(): React.ReactElement {
  const [state, setState] = useState<ListState>({ status: "loading" });
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [audience, setAudience] = useState<"all_tenants" | "active_tenants" | "trial_tenants">("active_tenants");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const load = useCallback(async () => {
    setState({ status: "loading" });
    try { const r = await listAnnouncements(); setState({ status: "ready", rows: r.items }); }
    catch (e) { setState({ status: "error", message: friendlyPlatformError(e) }); }
  }, []);
  useEffect(() => { void load(); }, [load]);

  const send = async () => {
    setBusy(true); setErr(null);
    try {
      await createAnnouncement({ title: title.trim(), body: body.trim(), audience });
      setTitle(""); setBody("");
      setToast("公告已建立。實際發送由 worker queue 進行（即將推出）。");
      void load();
    } catch (e) { setErr(friendlyPlatformError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-border bg-bg-surface p-4">
        <h2 className="text-sm font-semibold flex items-center gap-2 text-text-primary"><Megaphone className="h-4 w-4 text-accent" /> 全平台公告</h2>
        <p className="text-2xs text-text-muted mt-1">建立全平台或特定狀態租戶的公告。建立後由背景 worker 透過 LINE 通知到對應的 super_admin。</p>
      </div>
      <div className="card p-4 sm:p-5 space-y-3">
        <h3 className="text-sm font-semibold text-text-primary">發送新公告</h3>
        <Field label="標題" required><input className="input" value={title} onChange={(e) => setTitle(e.target.value)} /></Field>
        <Field label="內容" required><textarea className="textarea" rows={4} value={body} onChange={(e) => setBody(e.target.value)} placeholder="公告詳細內容…" /></Field>
        <Field label="對象">
          <select className="select" value={audience} onChange={(e) => setAudience(e.target.value as typeof audience)}>
            <option value="all_tenants">所有單位</option>
            <option value="active_tenants">已啟用單位</option>
            <option value="trial_tenants">試用單位</option>
          </select>
        </Field>
        {err && <div className="rounded border border-danger/40 bg-danger/10 p-3 text-sm text-danger">{err}</div>}
        {toast && <div className="rounded border border-success/40 bg-success/10 p-3 text-sm text-success">{toast}</div>}
        <div className="flex justify-end">
          <button className="btn-primary text-sm" onClick={() => void send()} disabled={busy || !title.trim() || !body.trim()}>
            <Send className="h-4 w-4" /> {busy ? "建立中…" : "建立公告"}
          </button>
        </div>
      </div>

      <div>
        <h3 className="text-sm font-semibold text-text-primary mb-3">公告紀錄</h3>
        {state.status === "loading" && <div className="space-y-2">{[0,1,2].map(i => <div key={i} className="card p-4"><Skeleton className="h-12 w-full" /></div>)}</div>}
        {state.status === "error" && <EmptyState icon={ServerCrash} title="公告列表載入失敗" description={state.message}
          action={<button className="btn-primary text-sm" onClick={() => void load()}>再試一次</button>} />}
        {state.status === "ready" && state.rows.length === 0 && <EmptyState icon={Inbox} title="尚未建立過公告" description="建立第一則公告後，紀錄會在這裡顯示。" />}
        {state.status === "ready" && state.rows.length > 0 && (
          <ul className="space-y-2">
            {state.rows.map((a) => (
              <li key={a.id} className="card p-4">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="font-medium text-text-primary">{a.title}</div>
                  <div className="text-2xs font-mono text-text-muted">{formatTaipeiDateTime(a.created_at)}</div>
                </div>
                <p className="mt-1 text-xs text-text-secondary whitespace-pre-wrap">{a.body}</p>
                <div className="mt-2 flex items-center gap-2">
                  <span className="badge-default text-2xs">對象：{a.audience}</span>
                  {a.sent_at ? <span className="badge-success text-2xs">已發送 · {a.reach_count ?? 0} 人</span> : <span className="badge-warning text-2xs">待發送</span>}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
